import { useState, useMemo } from 'react';
import {
  Box,
  Stack,
  Typography,
  Paper,
  Divider,
  Button,
  Alert,
  Chip,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  LinearProgress,
  Switch,
  FormControlLabel,
} from '@mui/material';
import {
  SendRounded,
  ScienceRounded,
  CheckCircleRounded,
  ErrorRounded,
  PeopleAltRounded,
  SubjectRounded,
  ScheduleSendRounded,
  EventRounded,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { blocksToHtml } from './utils';
import type { CampaignDraft, AudienceSegment, SendResult } from './types';
import { AUDIENCE_LABELS } from './audience-labels';
import { segmentPayload } from './segment-payload';
import { TenantDateTimeField } from '../../../components/TenantDateTimeField';
import { formatDateTime } from '../../../lib/i18n-format';
import { callable } from '../../../lib/callable';

// ─── Audience label helper ────────────────────────────────────────────────────

function audienceSummary(seg: AudienceSegment): string {
  const base = AUDIENCE_LABELS[seg.type] ?? seg.type;
  if (seg.type === 'tag' && seg.tag) return `Etiqueta: "${seg.tag}"`;
  if (seg.type === 'custom_contacts')
    return `${(seg.contactIds ?? []).length} contacto(s) seleccionado(s)`;
  return base;
}

// ─── Props ────────────────────────────────────────────────────────────────────

interface Step3SendProps {
  draft: CampaignDraft;
  tenantId: string;
  onSent: (result?: { scheduled?: boolean }) => void;
}

type SendPhase = 'idle' | 'sending' | 'scheduling' | 'done' | 'scheduled' | 'error';

/** Returns the minimum datetime string for the datetime-local input (now + 10 min). */
function minScheduledAt(): string {
  const d = new Date(Date.now() + 10 * 60 * 1000);
  return d.toISOString().slice(0, 16);
}

// ─── Component ────────────────────────────────────────────────────────────────

export function Step3Send({ draft, tenantId, onSent }: Step3SendProps) {
  const [phase, setPhase] = useState<SendPhase>('idle');
  const [result, setResult] = useState<SendResult | null>(null);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  // Scheduling
  const [scheduleMode, setScheduleMode] = useState(false);
  const [scheduledAt, setScheduledAt] = useState(minScheduledAt());

  // Test email
  const { t } = useTranslation('sis');
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmCount, setConfirmCount] = useState<number | null>(null);
  const [confirmLoading, setConfirmLoading] = useState(false);
  const [testLoading, setTestLoading] = useState(false);
  const [testSuccess, setTestSuccess] = useState(false);

  const bodyHtml = useMemo(() => blocksToHtml(draft.blocks), [draft.blocks]);

  /** Antes de mandar, preguntar — con el número que va a recibir de verdad.
   *
   *  NO HABÍA CONFIRMACIÓN: un clic despachaba a toda la audiencia. El
   *  historial de producción muestra el precio: tres campañas repetidas dos y
   *  tres veces con el mismo asunto, el rastro de alguien que no podía saber si
   *  había funcionado.
   *
   *  El número se pide AL SERVIDOR, al mismo resolvedor que hará el envío. Un
   *  «vas a escribirle a 52 personas» sólo sirve si es el 52 de verdad. */
  async function handleAskToConfirm() {
    if (scheduleMode) {
      await handleSchedule();
      return;
    }
    setConfirmOpen(true);
    setConfirmCount(null);
    setConfirmLoading(true);
    try {
      const countFn = callable<{ tenantId: string; audienceSegment: object }, { count: number }>(
        'countCampaignAudience',
      );
      const { data } = await countFn({
        tenantId,
        audienceSegment: segmentPayload(draft.audienceSegment),
      });
      setConfirmCount(data.count);
    } catch {
      // Si el conteo falla se sigue pudiendo enviar, pero el diálogo lo dice en
      // vez de mostrar un número inventado.
      setConfirmCount(null);
    } finally {
      setConfirmLoading(false);
    }
  }

  async function handleSend() {
    if (scheduleMode) {
      await handleSchedule();
      return;
    }

    setPhase('sending');
    setErrorMsg(null);
    try {
      const sendCampaignFn = callable<
        {
          tenantId: string;
          subject: string;
          htmlContent: string;
          audienceSegment: object;
          blocksJson: string;
          draftId?: string;
        },
        { sentCount: number; failedCount: number; failedRecipients: { email: string }[] }
      >('sendCampaign');

      const response = await sendCampaignFn({
        tenantId,
        subject: draft.subject,
        htmlContent: bodyHtml,
        audienceSegment: segmentPayload(draft.audienceSegment),
        // SE GUARDAN LOS BLOQUES TAMBIÉN AL ENVIAR DIRECTO. Sin esto, una
        // campaña que no pasó por borrador quedaba sin estructura y
        // «Reutilizar» no podía recuperarla nunca — sólo el envío programado
        // los mandaba.
        blocksJson: JSON.stringify(draft.blocks),
        ...(draft.draftId ? { draftId: draft.draftId } : {}),
      });

      setResult({
        sentCount: response.data.sentCount,
        failedCount: response.data.failedCount,
      });
      setPhase('done');
      onSent();
    } catch (err: any) {
      console.error('Campaign send error:', err);
      setErrorMsg(err.message || t('campaign.step3.sendFailed'));
      setPhase('error');
    }
  }

  async function handleSchedule() {
    const scheduledDate = new Date(scheduledAt);
    if (scheduledDate <= new Date()) {
      setErrorMsg(t('campaign.step3.mustBeFuture'));
      return;
    }

    setPhase('scheduling');
    setErrorMsg(null);
    try {
      const scheduleFn = callable<
        {
          tenantId: string;
          subject: string;
          htmlContent: string;
          audienceSegment: object;
          scheduledAt: string;
          blocksJson: string;
          draftId?: string;
        },
        { campaignId: string }
      >('scheduleCampaign');

      await scheduleFn({
        tenantId,
        subject: draft.subject,
        htmlContent: bodyHtml,
        audienceSegment: segmentPayload(draft.audienceSegment),
        // Send the tenant-local WALL-CLOCK; the server converts it to a UTC instant
        // in the tenant zone (was scheduledDate.toISOString() = the browser offset).
        scheduledAt,
        blocksJson: JSON.stringify(draft.blocks),
        ...(draft.draftId ? { draftId: draft.draftId } : {}),
      });

      setPhase('scheduled');
      onSent({ scheduled: true });
    } catch (err: any) {
      console.error('Campaign schedule error:', err);
      setErrorMsg(err.message || t('campaign.step3.scheduleFailed'));
      setPhase('error');
    }
  }

  /** LA PRUEBA VA AL CORREO DE QUIEN LA PIDE, y el servidor lo resuelve de su
   *  persona en el centro. Antes se mandaba la dirección escrita acá, lo que
   *  convertía el botón en un relé abierto: cualquiera podía despachar HTML a
   *  cualquier dirección con la marca del seminario. Ya no viaja ninguna. */
  async function handleSendTest() {
    setTestLoading(true);
    setTestSuccess(false);
    try {
      const sendTestCampaign = callable<
        {
          tenantId: string;
          subject: string;
          htmlContent: string;
        },
        { success: boolean }
      >('sendTestCampaign');

      await sendTestCampaign({
        tenantId,
        subject: draft.subject,
        htmlContent: bodyHtml,
      });
      setTestSuccess(true);
    } catch (err: any) {
      setErrorMsg(err.message || t('campaign.step3.testFailed'));
    } finally {
      setTestLoading(false);
    }
  }

  // ── Summary card ─────────────────────────────────────────────────────────────

  const SummaryCard = () => (
    <Paper variant="outlined" sx={{ borderRadius: 2, p: 3 }}>
      <Typography variant="subtitle2" fontWeight={700} mb={2} color="text.secondary">
        {t('campaign.step3.summary')}
      </Typography>
      <Stack spacing={1.5}>
        <Stack direction="row" spacing={1.5} alignItems="flex-start">
          <SubjectRounded fontSize="small" sx={{ color: 'text.secondary', mt: 0.25 }} />
          <Box>
            <Typography variant="caption" color="text.secondary">
              {t('campaign.tabs.subject')}
            </Typography>
            <Typography variant="body2" fontWeight={600}>
              {draft.subject}
            </Typography>
          </Box>
        </Stack>
        <Stack direction="row" spacing={1.5} alignItems="flex-start">
          <PeopleAltRounded fontSize="small" sx={{ color: 'text.secondary', mt: 0.25 }} />
          <Box>
            <Typography variant="caption" color="text.secondary">
              {t('campaign.tabs.audience')}
            </Typography>
            <Typography variant="body2">{audienceSummary(draft.audienceSegment)}</Typography>
          </Box>
        </Stack>
        {draft.estimatedCount !== undefined && (
          <Stack direction="row" spacing={1.5} alignItems="center">
            <ScheduleSendRounded fontSize="small" sx={{ color: 'text.secondary' }} />
            <Box>
              <Typography variant="caption" color="text.secondary">
                {t('campaign.step3.estimatedRecipients')}
              </Typography>
              <Chip
                label={`~${draft.estimatedCount} contactos`}
                size="small"
                color="primary"
                variant="outlined"
                sx={{ ml: 1 }}
              />
            </Box>
          </Stack>
        )}
        {draft.draftId && (
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Chip
              label={t('campaign.step3.draftSaved')}
              size="small"
              color="success"
              variant="outlined"
            />
          </Stack>
        )}
        <Divider />
        <Typography variant="caption" color="text.secondary">
          {draft.blocks.length} bloque(s) de contenido · El encabezado y pie de página se agregan
          automáticamente.
        </Typography>
      </Stack>
    </Paper>
  );

  // ── Sending state ─────────────────────────────────────────────────────────────

  if (phase === 'sending' || phase === 'scheduling') {
    return (
      <Stack spacing={3} maxWidth={500} mx="auto" alignItems="center" py={6}>
        <CircularProgress size={56} />
        <Typography variant="h6" fontWeight={600}>
          {phase === 'scheduling' ? t('campaign.step3.scheduling') : t('campaign.step3.sending')}
        </Typography>
        <LinearProgress sx={{ width: '100%', borderRadius: 4 }} />
        <Typography variant="body2" color="text.secondary" textAlign="center">
          {phase === 'scheduling'
            ? t('campaign.step3.savingForLater')
            : t('campaign.step3.sendingDetail')}
        </Typography>
      </Stack>
    );
  }

  // ── Done / Scheduled state ────────────────────────────────────────────────────

  if (phase === 'done' && result) {
    return (
      <Stack spacing={3} maxWidth={500} mx="auto" alignItems="center" py={6}>
        <CheckCircleRounded sx={{ fontSize: 64, color: 'success.main' }} />
        <Typography variant="h5" fontWeight={700}>
          {t('campaign.step3.sent')}
        </Typography>
        <Paper
          variant="outlined"
          sx={{ borderRadius: 3, p: 3, width: '100%', borderColor: 'success.main' }}
        >
          <Stack spacing={1.5}>
            <Stack direction="row" justifyContent="space-between">
              <Typography variant="body2" color="text.secondary">
                {t('campaign.step3.emailsSent')}
              </Typography>
              <Chip label={result.sentCount} color="success" size="small" />
            </Stack>
            {result.failedCount !== undefined && result.failedCount > 0 && (
              <Stack direction="row" justifyContent="space-between">
                <Typography variant="body2" color="text.secondary">
                  {t('campaign.tabs.failed')}
                </Typography>
                <Chip label={result.failedCount} color="error" size="small" />
              </Stack>
            )}
            <Divider />
            <Typography variant="caption" color="text.secondary">
              {t('campaign.step3.sentRecorded')}
            </Typography>
          </Stack>
        </Paper>
      </Stack>
    );
  }

  if (phase === 'scheduled') {
    return (
      <Stack spacing={3} maxWidth={500} mx="auto" alignItems="center" py={6}>
        <EventRounded sx={{ fontSize: 64, color: 'primary.main' }} />
        <Typography variant="h5" fontWeight={700}>
          {t('campaign.step3.scheduled')}
        </Typography>
        <Paper
          variant="outlined"
          sx={{ borderRadius: 3, p: 3, width: '100%', borderColor: 'primary.main' }}
        >
          <Stack spacing={1}>
            <Typography variant="body2" color="text.secondary" textAlign="center">
              {t('campaign.step3.scheduledFor')}
            </Typography>
            <Typography variant="body1" fontWeight={600} textAlign="center">
              {formatDateTime(scheduledAt, {
                dateStyle: 'full',
                timeStyle: 'short',
              })}
            </Typography>
            <Divider sx={{ my: 1 }} />
            <Typography variant="caption" color="text.secondary" textAlign="center">
              {t('campaign.step3.cancelFromTab')}
            </Typography>
          </Stack>
        </Paper>
      </Stack>
    );
  }

  // ── Error state ───────────────────────────────────────────────────────────────

  if (phase === 'error') {
    return (
      <Stack spacing={3} maxWidth={500} mx="auto" py={4}>
        <Stack direction="row" spacing={2} alignItems="center">
          <ErrorRounded sx={{ fontSize: 48, color: 'error.main' }} />
          <Box>
            <Typography variant="h6" fontWeight={700} color="error">
              {t('campaign.step3.sendError')}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {errorMsg}
            </Typography>
          </Box>
        </Stack>
        <Button
          variant="outlined"
          onClick={() => {
            setPhase('idle');
            setErrorMsg(null);
          }}
        >
          {t('campaign.step3.tryAgain')}
        </Button>
      </Stack>
    );
  }

  // ── Idle (pre-send) ───────────────────────────────────────────────────────────

  return (
    <Stack spacing={3} maxWidth={900} mx="auto">
      <Box>
        <Typography variant="subtitle1" fontWeight={700} mb={0.5}>
          {t('campaign.step3.confirmAndSend')}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {t('campaign.step3.reviewBeforeSending')}
        </Typography>
      </Box>

      <SummaryCard />

      {/* Test email */}
      <Paper variant="outlined" sx={{ borderRadius: 2, p: 2.5 }}>
        <Typography variant="subtitle2" fontWeight={600} mb={1.5}>
          {t('campaign.step3.sendTest')}
        </Typography>
        <Typography variant="body2" color="text.secondary" mb={2}>
          {t('campaign.step3.sendTestHelp')}
        </Typography>
        <Button
          variant="outlined"
          startIcon={
            testLoading ? (
              <CircularProgress size={16} color="inherit" />
            ) : (
              <ScienceRounded fontSize="small" />
            )
          }
          onClick={handleSendTest}
          disabled={testLoading}
          sx={{ borderRadius: 2 }}
        >
          {testLoading ? 'Enviando...' : 'Enviar prueba a mi correo'}
        </Button>
        {testSuccess && (
          <Alert severity="success" sx={{ mt: 1.5, py: 0 }}>
            {t('campaign.step3.testSent')}
          </Alert>
        )}
      </Paper>

      {/* Schedule toggle */}
      <Paper variant="outlined" sx={{ borderRadius: 2, p: 2.5 }}>
        <FormControlLabel
          control={
            <Switch
              checked={scheduleMode}
              onChange={(e) => setScheduleMode(e.target.checked)}
              color="primary"
            />
          }
          label={
            <Box>
              <Typography variant="subtitle2" fontWeight={600}>
                {t('campaign.step3.scheduleLater')}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {t('campaign.step3.scheduleHelp')}
              </Typography>
            </Box>
          }
        />

        {scheduleMode && (
          <Box sx={{ mt: 2 }}>
            <TenantDateTimeField
              label={t('campaign.step3.sendAt')}
              fullWidth
              value={scheduledAt}
              onChange={setScheduledAt}
              minDate={minScheduledAt().slice(0, 10)}
              helperText={t('campaign.step3.sendAtHelp')}
            />
          </Box>
        )}
      </Paper>

      {errorMsg && (
        <Alert severity="error" onClose={() => setErrorMsg(null)}>
          {errorMsg}
        </Alert>
      )}

      {/* Send button */}
      <Stack direction="row" justifyContent="flex-end">
        <Button
          variant="contained"
          size="large"
          startIcon={scheduleMode ? <EventRounded /> : <SendRounded />}
          onClick={handleAskToConfirm}
          sx={{ borderRadius: 2, px: 5, fontWeight: 700 }}
        >
          {scheduleMode ? t('campaign.step3.scheduleCampaign') : t('campaign.step3.sendCampaign')}
        </Button>
      </Stack>

      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>{t('campaign.confirm.title')}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            {t('campaign.confirm.subject')}
          </Typography>
          <Typography variant="body1" fontWeight={600} mb={2}>
            {draft.subject}
          </Typography>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            {t('campaign.confirm.recipients')}
          </Typography>
          {confirmLoading ? (
            <Stack direction="row" spacing={1} alignItems="center">
              <CircularProgress size={16} />
              <Typography variant="body2">{t('campaign.confirm.counting')}</Typography>
            </Stack>
          ) : confirmCount === null ? (
            <Alert severity="warning" sx={{ py: 0 }}>
              {t('campaign.confirm.countFailed')}
            </Alert>
          ) : (
            <Typography variant="h6" fontWeight={700}>
              {t('campaign.confirm.people', { count: confirmCount })}
            </Typography>
          )}
          <Typography variant="body2" color="text.secondary" mt={2}>
            {t('campaign.confirm.irreversible')}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)}>{t('campaign.confirm.cancel')}</Button>
          <Button
            variant="contained"
            disabled={confirmLoading || confirmCount === 0}
            onClick={() => {
              setConfirmOpen(false);
              void handleSend();
            }}
          >
            {confirmCount === null
              ? t('campaign.confirm.sendWithoutCount')
              : t('campaign.confirm.send')}
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}
